import React, { FC } from 'react'
import { generatePath, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Heading, HeadingLevel, Text, TextVariant } from '@enbw/ui-react'
import { Contract } from '../types'
import styles from './styles.module.scss'
import { ContractStatusLabel } from '@/components/ContractStatusLabel'
import { Paths } from '@/paths'
import { valueOrEmpty } from '@/util/string-util'

type ResultsTableProps = {
  readonly contracts: Contract[]
}

const buildContractPath = (contractNumber: string): string => generatePath(Paths.CONTRACT_DETAILS, { contractNumber })

export const ResultsTable: FC<ResultsTableProps> = ({ contracts }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const openContractInNewTab = (contractNumber: string): void => {
    window.open(buildContractPath(contractNumber), '_blank', 'noopener,noreferrer')
  }

  const handleRowClick = (event: React.MouseEvent<HTMLTableRowElement>, contractNumber: string): void => {
    if (globalThis.getSelection()?.toString()) return
    if (event.ctrlKey || event.metaKey) {
      openContractInNewTab(contractNumber)
      return
    }
    navigate(buildContractPath(contractNumber))
  }

  const handleRowMouseDown = (event: React.MouseEvent<HTMLTableRowElement>): void => {
    if (event.button === 1) {
      event.preventDefault()
    }
  }

  const handleRowAuxClick = (event: React.MouseEvent<HTMLTableRowElement>, contractNumber: string): void => {
    if (event.button !== 1) return
    openContractInNewTab(contractNumber)
  }

  const handleRowKeyDown = (event: React.KeyboardEvent<HTMLTableRowElement>, contractNumber: string): void => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    navigate(buildContractPath(contractNumber))
  }

  return (
    <div className={styles.tableContainer}>
      <table className={styles.resultsTable}>
        <thead>
          <tr>
            <th>
              <Heading level={HeadingLevel.H6}>{t('contracts.resultsTable.contractNumber')}</Heading>
            </th>
            <th>
              <Heading level={HeadingLevel.H6}>{t('contracts.resultsTable.emaId')} </Heading>
            </th>
            <th className={styles.narrowHeader}>
              <Heading level={HeadingLevel.H6}>{t('contracts.resultsTable.billingAccountNumber')} </Heading>
            </th>
            <th className={styles.narrowHeader}>
              <Heading level={HeadingLevel.H6}>{t('contracts.resultsTable.salesPartnerCustomerReference')}</Heading>
            </th>
            <th>
              <Heading level={HeadingLevel.H6}>{t('contracts.resultsTable.email')}</Heading>
            </th>
            <th>
              <Heading level={HeadingLevel.H6}>{t('contracts.resultsTable.firstName')}</Heading>
            </th>
            <th>
              <Heading level={HeadingLevel.H6}>{t('contracts.resultsTable.lastName')}</Heading>
            </th>
            <th>
              <Heading level={HeadingLevel.H6}>{t('contracts.resultsTable.streetAndHouseNumber')}</Heading>
            </th>
            <th>
              <Heading level={HeadingLevel.H6}>{t('contracts.resultsTable.postalCodeAndCity')}</Heading>
            </th>
            <th>
              <Heading level={HeadingLevel.H6}>{t('contracts.resultsTable.status')}</Heading>
            </th>
          </tr>
        </thead>
        <tbody>
          {contracts.map((contract) => (
            <tr
              key={contract.contractId}
              role="link"
              tabIndex={0}
              aria-label={`Vertrag ${contract.contractNumber} anzeigen`}
              onMouseDown={handleRowMouseDown}
              onClick={(event) => handleRowClick(event, contract.contractNumber!)}
              onAuxClick={(event) => handleRowAuxClick(event, contract.contractNumber!)}
              onKeyDown={(event) => handleRowKeyDown(event, contract.contractNumber!)}
            >
              <td>
                <Text variant={TextVariant.BodySmall}>{valueOrEmpty(contract.contractNumber)}</Text>
              </td>
              <td>
                <Text variant={TextVariant.BodySmall}>{contract.emaId}</Text>
              </td>
              <td>
                <Text variant={TextVariant.BodySmall}>{valueOrEmpty(contract.billingAccountId)}</Text>
              </td>
              <td title={valueOrEmpty(contract.salesPartnerCustomerReference)}>
                <Text variant={TextVariant.BodySmall}>{valueOrEmpty(contract.salesPartnerCustomerReference)}</Text>
              </td>
              <td title={contract.email}>
                <Text variant={TextVariant.BodySmall}>{contract.email}</Text>
              </td>
              <td>
                <Text variant={TextVariant.BodySmall}>{contract.firstName}</Text>
              </td>
              <td>
                <Text variant={TextVariant.BodySmall}>{contract.lastName}</Text>
              </td>
              <td>
                <Text variant={TextVariant.BodySmall}>{contract.streetAndHouseNumber}</Text>
              </td>
              <td>
                <Text variant={TextVariant.BodySmall}>{contract.postalCodeAndCity}</Text>
              </td>
              <td>
                <ContractStatusLabel status={contract.contractStatus} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
